// Table primitives shared by the My Trades tables (index.tsx and
// UnifiedTradesTable). Extracted from index.tsx so the unified table can
// import them without a module cycle. MoneyCell/PnlCell are the single home
// for the money-column rules both cross-venue tables must agree on: masked
// under the #124 P&L privacy preference, and '—' for unavailable values —
// never a fabricated $0.00.

import type { ReactNode } from 'react';
import { maskPnl, maskPnlColor } from '../../lib/pnlMask';
import { formatMoney } from '../../lib/infra/formatters';

export function Th({
  children,
  align = 'left',
  title,
  sub,
}: {
  children: ReactNode;
  align?: 'left' | 'right' | 'center';
  title?: string;
  /** Optional second-row qualifier (e.g. "estimated", "actual"). Renders
   *  smaller, normal-case, dimmed beneath the main header. Lets us
   *  reclaim horizontal space when the qualifier is repeated across
   *  several columns. */
  sub?: string;
}) {
  const alignCls =
    align === 'right'
      ? 'text-right'
      : align === 'center'
        ? 'text-center'
        : 'text-left';
  return (
    <th
      className={`whitespace-nowrap px-3 py-2 align-top text-[11px] font-semibold uppercase tracking-wider ${alignCls}`}
      title={title}
    >
      <div>{children}</div>
      {sub && (
        <div className="-mt-0.5 text-[9px] font-normal normal-case tracking-normal opacity-60">
          {sub}
        </div>
      )}
    </th>
  );
}

export function formatDateOnly(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Right-aligned dollar cell. */
export function MoneyCell({
  dollars,
  hidePnl,
}: {
  dollars: number | null;
  hidePnl: boolean;
}) {
  return (
    <td className="text-muted-foreground px-3 py-1.5 text-right font-mono">
      {maskPnl(hidePnl, dollars == null ? '—' : formatMoney(dollars))}
    </td>
  );
}

/** Net P&L cell — signed, green/red/neutral by sign, muted when unknown. */
export function PnlCell({
  dollars,
  hidePnl,
}: {
  dollars: number | null;
  hidePnl: boolean;
}) {
  return (
    <td
      className={`px-3 py-1.5 text-right font-mono font-medium ${maskPnlColor(
        hidePnl,
        dollars == null
          ? 'text-muted-foreground'
          : dollars > 0
            ? 'text-green-400'
            : dollars < 0
              ? 'text-red-400'
              : 'text-foreground',
      )}`}
    >
      {maskPnl(hidePnl, dollars == null ? '—' : formatMoney(dollars, { showSign: true }))}
    </td>
  );
}
